import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import models from "../../model-declaration";
import { formatUnderscoreToSpace } from "../../utils/text-format";

const Sidebar = ({ isMobileOpen, setIsMobileOpen }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const currentPath = location.pathname.split("/")[1];

  const getItemStyles = (pathSegment, collapsed) => {
    const isActive = currentPath === pathSegment;
    return `group relative z-10 flex items-center rounded-lg py-3 text-sm font-medium transition-all duration-300 ${
      collapsed
        ? "justify-center px-0 mx-auto w-10"
        : "justify-between mx-2 px-3"
    } ${
      isActive
        ? "bg-primary text-white shadow-lg shadow-primary/30"
        : "text-slate-600 hover:bg-slate-100 hover:text-primary"
    }`;
  };

  const getIconStyles = (pathSegment) => {
    const isActive = currentPath === pathSegment;
    return `h-5 w-5 shrink-0 transition-transform duration-300 group-hover:scale-110 ${
      isActive ? "text-white" : "text-slate-500 group-hover:text-primary"
    }`;
  };

  const renderIcon = (iconProp, pathSegment) => {
    let IconComponent;
    if (typeof iconProp === "string") {
      IconComponent = LucideIcons[iconProp] || LucideIcons.Database;
    } else if (iconProp) {
      IconComponent = iconProp;
    } else {
      IconComponent = LucideIcons.Database;
    }

    return <IconComponent className={getIconStyles(pathSegment)} />;
  };

  const navContent = (collapsed = false) => (
    <div className="flex flex-col h-full relative z-10 overflow-y-auto no-scrollbar">
      {/* Management Section */}
      {!collapsed && (
        <p className="px-6 text-[10px] font-bold text-slate-400 uppercase tracking-[2px] mb-2 mt-2">
          Management
        </p>
      )}
      <div className="space-y-1">
        <NavLink
          to="/dashboard"
          className={getItemStyles("dashboard", collapsed)}
          onClick={() => setIsMobileOpen(false)}
          title={collapsed ? "Dashboard" : ""}
        >
          <div className="flex items-center gap-3 truncate">
            {renderIcon("LayoutDashboard", "dashboard")}
            {!collapsed && <span className="truncate">Dashboard</span>}
          </div>
          {!collapsed && currentPath === "dashboard" && (
            <LucideIcons.ChevronRight className="h-4 w-4 text-white/80 shrink-0" />
          )}
        </NavLink>

        <NavLink
          to="/admin/users/list"
          className={getItemStyles("admin", collapsed)}
          onClick={() => setIsMobileOpen(false)}
          title={collapsed ? "Staff / Admins" : ""}
        >
          <div className="flex items-center gap-3 truncate">
            {renderIcon("Users", "admin")}
            {!collapsed && <span className="truncate">Staff / Admins</span>}
          </div>
          {!collapsed && currentPath === "admin" && (
            <LucideIcons.ChevronRight className="h-4 w-4 text-white/80 shrink-0" />
          )}
        </NavLink>
      </div>

      <div className="mx-4 my-3 border-t border-slate-100" />

      {/* Dynamic Data Models Section */}
      {models && models.length > 0 && (
        <div className="flex-1 space-y-1">
          {!collapsed && (
            <p className="px-6 text-[10px] font-bold text-slate-400 uppercase tracking-[2px] mb-2">
              Data Models
            </p>
          )}
          {models.map((model, index) => {
            const tableName = model?.tableName;
            return (
              <NavLink
                key={index}
                to={`/${tableName}/list`}
                className={getItemStyles(tableName, collapsed)}
                onClick={() => setIsMobileOpen(false)}
                title={collapsed ? formatUnderscoreToSpace(tableName) : ""}
              >
                <div className="flex items-center gap-3 truncate">
                  {renderIcon(model?.icon, tableName)}
                  {!collapsed && (
                    <span className="capitalize truncate">
                      {formatUnderscoreToSpace(tableName)}
                    </span>
                  )}
                </div>
                {!collapsed && currentPath === tableName && (
                  <LucideIcons.ChevronRight className="h-4 w-4 text-white/80 shrink-0" />
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm transition-opacity lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-white p-4 shadow-2xl transition-transform duration-300 ease-in-out lg:hidden ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-end pb-2 relative z-20">
          <button
            onClick={() => setIsMobileOpen(false)}
            className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <LucideIcons.X className="h-5 w-5" />
          </button>
        </div>
        {navContent(false)}
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden h-full flex-col border-r border-slate-100 bg-white py-6 transition-all duration-300 ease-in-out lg:flex relative rounded-2xl shadow-sm ${
          isCollapsed ? "w-20" : "w-64"
        }`}
      >
        {/* Floating Collapse Toggle Button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-2 top-2 bg-white border border-slate-200 rounded-full p-1 shadow-sm text-darkText hover:text-primary hover:scale-110 z-20 transition-all"
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          {isCollapsed ? (
            <LucideIcons.ChevronRight size={16} />
          ) : (
            <LucideIcons.ChevronLeft size={16} />
          )}
        </button>

        {navContent(isCollapsed)}
      </aside>
    </>
  );
};

export default Sidebar;
